import React, {useEffect} from "react";
import {Button, CircularProgress, FormControlLabel, Grid, IconButton, LinearProgress, Paper, Radio, RadioGroup, Typography, withTheme} from "@material-ui/core";
import {ArrowBack, BarChart, ChevronRight, Notifications} from "@material-ui/icons";
import {useGlobalStyles} from "../../catalogmanager/utils/styles";
import {makeStyles} from "@material-ui/core/styles";
import Helpers, {StorageHelper} from "../../helpers";
import EnhancedErrorDialog from "../../catalogmanager/components/EnhancedErrorDialog";
import EnhancedSelect from "../../catalogmanager/components/EnhancedSelect";
import {utilClearSkuMatches, utilDeleteAllInventory, utilGetPartners, utilImportInventory, utilResendInventory, utilZeroOutAllInventory} from "../../catalogmanager/utils/utils";
import DownloadButton from "../../general/download_button";
import DropZone from "../../files/dropzone";
import EnhancedConfirmDialog from "../../catalogmanager/components/EnhancedConfirmDialog";
import Auth from "../../auth";
import {FileDownloadIcon, InventoryFaq, ListCheckmark, SpreadsheetIcon} from "../../catalogmanager/utils/icons";
import axios from "axios";
import Config from "../../config";
import FileSaver from 'filesaver.js-npm';
import moment from "moment";
import UpdatingDialog from "../../catalogmanager/components/Dialogs/UpdatingDialog";
import GenericDialog from "../../catalogmanager/components/Dialogs/GenericDialog";

//region Styles and StyledComponents
export const useAdvancedExportStyles = makeStyles(theme => ({
    sectionTitle: {
        fontSize: "18px",
        fontWeight: 700,
    },
    filterHeader: {
        color: "#7F7D83",
        fontWeight: 700
    }
}));
//endregion

const blankProfile = {
    Name: '',
    Delimiter: ",",
    FileType: "inventoryFeed",
    OutputFormat: "csv",
    Partner: "",
    Filter: undefined,
    Fields: [],
    Rules: []
};

const storedIdKey = "LBInventoryImportPartnerId";

function InventoryManagementImport(props) {
    const {isBillable} = props;
    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");

    const globalClasses = useGlobalStyles();
    const classes = useAdvancedExportStyles();
    const storageHelper = new StorageHelper();

    //region Component State
    const [error, setError] = React.useState({open: false, errorHeader: "", errorMessage: ""});
    const [loading, setLoading] = React.useState(false);
    const [updating, setUpdating] = React.useState(false);
    const [updateMessage, setUpdateMessage] = React.useState("");
    const [updated, setUpdated] = React.useState(false);

    const [partnerId, setPartnerId] = React.useState(undefined);
    const [partners, setPartners] = React.useState([]);
    const [profile, setProfile] = React.useState(blankProfile);

    const [confirmDeleteAllItems, setConfirmDeleteAllItems] = React.useState(false);
    const [confirmZeroOutItems, setConfirmZeroOutItems] = React.useState(false);
    const [confirmReuploadAllItems, setConfirmReuploadAllItems] = React.useState(false);
    const [confirmClearAllSkuMatches, setConfirmClearAllSkuMatches] = React.useState(false);
    const [confirmValidationBypass, setConfirmValidationBypass] = React.useState(false);
    const [validationWarning, setValidationWarning] = React.useState("");

    const [retailerFields, setRetailerFields] = React.useState([]);
    const [downloadingTemplate, setDownloadingTemplate] = React.useState(false);
    const [file, setFile] = React.useState(null);

    const [helpDesks, setHelpDesks] = React.useState([]);
    const [ediSpecifications, setEdiSpecifications] = React.useState([]);
    //endregion

    //region Functions
    const importItems = async (bypassValidation = false) => {
        if (!hasInventoryManagePermission) return;

        setUpdating(true);
        setUpdateMessage("Importing items...");

        try {
            const transform = profile.FileType === 'standard' ? false : true;
            const result = await utilImportInventory(partnerId, file, transform, profile.FileType, bypassValidation);

            // Prompt the user to allow importing anyway even if we failed validations.
            if (result !== true && result.errorHeader === "Validation Warning") {
                setConfirmValidationBypass(true);
                setUpdating(false);
                setValidationWarning(result.errorMessage);
                return;
            }


            if (result !== true && result.errorMessage) {
                setError({
                    open: true,
                    errorHeader: "Error Importing Items",
                    errorMessage: result.errorMessage
                });
            }
            else {
                setUpdated(true);
            }
        } catch (err) {
            setError({
                open: true,
                errorHeader: "Error Importing Items",
                errorMessage: err.message
            });
        }
        
        setUpdating(false);
    }

    const loadPartners = async () => {
        if (!hasInventoryManagePermission) return;

        setLoading(true);
        let partners = await utilGetPartners();
        if (partners.errorMessage) return;

        let sortedPartners = partners.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        setPartners(sortedPartners);

        let storedId = storageHelper.get(storedIdKey);
        let partnerId = storedId || sortedPartners[0].Id;
        setPartnerId(partnerId);
        setProfile({...profile, Partner: sortedPartners.find(p => p.Id === partnerId).CompanyName});
        setLoading(false);
    }

    const removeAllItems = async () => {
        if (!hasInventoryManagePermission) return;

        setUpdating(true);
        setUpdateMessage("Removing all items from the feed...");
        try {
            const response = await utilDeleteAllInventory(partnerId);
            if (response !== true && response.errorMessage) {
                setError({
                    open: true,
                    errorHeader: "Error Removing Items",
                    errorMessage: response.errorMessage
                });
            }
        } catch (err) {
            setError({
                open: true,
                errorHeader: "Error Removing Items",
                errorMessage: err.message
            });
        }
        setUpdating(false);
    }

    const zeroOutItems = async () => {
        if (!hasInventoryManagePermission) return;
        setUpdating(true);
        setUpdateMessage("Zeroing out all items from the feed...");
        const response = await utilZeroOutAllInventory(partnerId);
        if (response !== true && response.errorMessage) {
            setError({
                open: true,
                errorHeader: "Error Zeroing Out Items",
                errorMessage: response.errorMessage
            });
        }
        setUpdating(false);
    }

    const reuploadAllItems = async () => {
        if (!hasInventoryManagePermission) return;

        setUpdating(true);
        setUpdateMessage("Reuploading all items from the feed...");
        const response = await utilResendInventory(partnerId);
        if (response !== true && response.errorMessage) {
            setError({
                open: true,
                errorHeader: "Error Reuploading Items",
                errorMessage: response.errorMessage
            });
        }
        setUpdating(false);
    }

    const loadEdiSpecifications = async () => {
        try {
            let url = Config.api + '/odata/Company/EDIPartnerSpecs';
            let response = await axios.get(url);
            const specifications = response.data.value;
            setEdiSpecifications(specifications);
        } catch (err) {
            setError({
                open: true,
                errorHeader: "Error Loading EDI Specifications",
                errorMessage: err.message
            });
        }
    }

    const clearAllSkuMatches = async () => {
        if (!isBillable) return;
        if (!hasInventoryManagePermission) return;

        setUpdating(true);
        setUpdateMessage("Clearing all SKU matches...");
        const response = await utilClearSkuMatches(partnerId);
        if (response !== true && response.errorMessage) {
            setError({
                open: true,
                errorHeader: "Error Clearing SKU Matches",
                errorMessage: response.errorMessage
            });
        }
        setUpdating(false);
    }

    const getValidationState = async () => {
        let coid = props.isRetailer ? Auth.getAccountNumber() : partnerId;
        let rules = (((await axios.get(Config.api + "/odata/Company/ValidationDtos?$filter=DocumentType eq 'Inventory' and CoId eq " + coid)).data.value || [])[0] || {}).Rules || [];
        if (rules.length > 0) {
            let requiredFields = ["SupplierSKU", "Quantity"];
            for (let i = 0; i < rules.length; i++) {
                if (requiredFields.indexOf(rules[i].Field) === -1) {
                    requiredFields.push(rules[i].Field);
                }
            }
            setRetailerFields(requiredFields);
        }
    }

    const downloadTemplate = async (templateType) => {
        setDownloadingTemplate(true);
        let requiredFields = ["SupplierSKU", "MerchantSKU"];
        if (templateType === "standard") {
            let requestUrl = Config.api + `/api/v1/Inventory/${partnerId}/?mapped=${false}&transform=${false}&includeNullQuantity=true&fileType=${"csv"}`;
            requestUrl += "&modifiedAfter=" + new Date(new Date().getTime() + 10000).toISOString();
            let url = (await axios.get(requestUrl)).data.Body;
            let suffix = "unmapped";

            const data = {
                href: url,
                fileName: moment()
                    // eslint-disable-next-line no-useless-concat
                    .format('YYYYMMDDHHmmss') + "-" + suffix + "." + "csv"
            };

            const response = await fetch(data.href);
            const blob = await response.blob();
            FileSaver.saveAs(blob, data.fileName);
            setDownloadingTemplate(false);
            return;
        } else if (templateType === "retailer") {
            requiredFields = retailerFields;
        }
        let csv = requiredFields.join(",") + "\n\n";
        let blob = new Blob([csv], {type: "application/octet-stream"});
        FileSaver.saveAs(blob, `${templateType}_template.csv`);
        setDownloadingTemplate(false);
    }

    const onUploadDrop = async (files) => {
        if (!files || !files.length) {
            return;
        }
        // if (!props.isBillable) {
        //     setProfile({...profile, FileType: 'customFeed'});
        // }
        setFile(files[0]);
    }

    const loadHelpDesks = async () => {
        try {
            let res = (await axios.get(Config.api + "/odata/Company/Functions.GetCompanyInfo")).data;
            let helpDesks = (res.Helpdesks || []).sort((a, b) => a.CompanyName > b.CompanyName ? 1 : -1);
            setHelpDesks(helpDesks);
        } catch (e) {
            let error = null;
            if (e.response && e.response.data) {
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            if (!error && !e.response) {
                error = "Request failed: " + e.message;
            }
            
            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: error
            });

            return;
        }
    }
    //endregion

    useEffect(() => {
        loadPartners();
        loadHelpDesks();
        loadEdiSpecifications();

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        getValidationState();

        // eslint-disable-next-line
    }, [partnerId]);

    return (
        <>
            <Grid>
                <LinearProgress
                    classes={{
                        colorPrimary: globalClasses.progressBar,
                        barColorPrimary: globalClasses.progressBarBackground
                    }}
                    style={{
                        width: "calc(100% + 2em)",
                        height: '0.4em',
                        position: "relative",
                        top: "-1em",
                        right: "1em",
                        visibility: updating ? undefined : 'hidden'
                    }}
                    type='indeterminate'/>

                <Grid container style={{gap: "24px", padding: "24px 14px"}}>
                    <Grid item container xs={12} justify={"flex-start"} style={{gap: "16px"}}>
                        <IconButton className={globalClasses.iconButton}
                                    style={{height: "36px", width: "36px"}}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (props.location.key === undefined)
                                            props.history.push("/advanced-product-management/v2");
                                        else
                                            props.history.goBack();
                                    }}>
                            <ArrowBack style={{height: "18px", width: "18px"}}/>
                        </IconButton>

                        <Typography style={{fontSize: "24px", fontWeight: "600"}}>
                            Import
                        </Typography>
                    </Grid>

                    <Grid item container xs={12} justify={"flex-start"} style={{gap: "16px"}}>
                        <Paper style={{width: "100%", borderRadius: "5px", padding: "24px"}}>
                            <Grid item container>
                                <Typography className={classes.sectionTitle}>
                                    Partner
                                </Typography>
                            </Grid>

                            <Grid item container style={{gap: "12px", flexDirection: "column", textAlign: "left"}}>
                                <EnhancedSelect
                                    isClearable
                                    loading={loading}
                                    value={{value: profile.Partner, label: profile.Partner}}
                                    options={partners.map(partner => ({
                                        value: partner.CompanyName,
                                        label: partner.CompanyName
                                    }))}
                                    onChange={(e) => {
                                        if (e == null) {
                                            setPartnerId("");
                                            setProfile({...profile, Partner: ""});
                                            return;
                                        }

                                        const selectedPartner = partners.find(partner => partner.CompanyName === e.value);
                                        if (selectedPartner !== undefined) {
                                            setPartnerId(selectedPartner.Id);
                                            setProfile({...profile, Partner: selectedPartner.CompanyName});
                                        }
                                    }}
                                />

                                {(partnerId === undefined || partnerId === "") && (
                                    <Typography>
                                        Please select a partner to import inventory for.
                                    </Typography>
                                )}
                            </Grid>
                        </Paper>
                    </Grid>

                    <Grid container>
                        <Grid container item xs={7}
                              style={{paddingRight: "14px", gap: "14px", flexDirection: "column"}}>
                            <Grid item container xs={12} justify={"flex-start"} style={{gap: "16px", flex: 0}}>
                                <Paper style={{width: "100%", borderRadius: "5px", padding: "24px"}}>
                                    <Grid item container style={{marginBottom: "16px"}}>
                                        <Typography className={classes.sectionTitle}>
                                            Feed Type
                                        </Typography>
                                    </Grid>

                                    <Grid item container style={{gap: "12px", textAlign: "left"}}>
                                        <RadioGroup
                                            value={profile.FileType}
                                            onChange={(e) => setProfile({...profile, FileType: e.target.value})}
                                            style={{display: "flex", flexDirection: "row", gap: "16px"}}
                                        >
                                            <FormControlLabel
                                                value="inventoryFeed"
                                                control={<Radio/>}
                                                label={
                                                    <Grid container style={{flexDirection: "column"}}>
                                                        <Typography style={{fontWeight: 500}}>
                                                            Inventory Feed
                                                        </Typography>
                                                        <Typography style={{fontWeight: 400, color: "#7F7D83"}}>
                                                            Select this feed if you are importing a file with required
                                                            SKU information.
                                                        </Typography>
                                                    </Grid>
                                                }
                                            />
                                            <FormControlLabel
                                                value="customFeed"
                                                control={<Radio/>}
                                                label={
                                                    <Grid container style={{flexDirection: "column"}}>
                                                        <Typography style={{fontWeight: 500}}>
                                                            Custom Feed (Not Common)
                                                        </Typography>
                                                        <Typography style={{fontWeight: 400, color: "#7F7D83"}}>
                                                            Select this feed if you have a custom mapping with
                                                            Logicbroker to be able to import your own file format mapped
                                                            to the Logicbroker fields. </Typography>
                                                    </Grid>
                                                }
                                            />
                                            <FormControlLabel
                                                value="matchedSkuFeed"
                                                control={<Radio/>}
                                                label={
                                                    <Grid container style={{flexDirection: "column"}}>
                                                        <Typography style={{fontWeight: 500}}>
                                                            Matched SKUs Feed
                                                        </Typography>
                                                        <Typography style={{fontWeight: 400, color: "#7F7D83"}}>
                                                            Select this feed if you are importing a file with the
                                                            Supplier and Retailer SKU. Only products with both SKU are
                                                            sent to the retailer. </Typography>
                                                    </Grid>
                                                }
                                            />
                                        </RadioGroup>
                                    </Grid>
                                </Paper>

                                <Paper style={{width: "100%", padding: "24px", borderRadius: "5px"}}>
                                    <Grid container>
                                        <Grid container style={{
                                            justifyContent: "center",
                                            alignItems: "flex-start",
                                            gap: "10px",
                                            marginBottom: "10px",
                                            flexDirection: "column"
                                        }}>
                                            <Typography variant="h6">
                                                Upload File
                                            </Typography>
                                        </Grid>

                                        <Grid container>
                                            <DropZone
                                                accept=".csv, .xlsx"
                                                style={{
                                                    width: '100%',
                                                    height: '10em',
                                                    border: "1px dashed #93AAFD",
                                                    borderRadius: "4px"
                                                }}
                                                onDrop={onUploadDrop}
                                            >
                                                {({isDragAccept, isDragReject, acceptedFiles, rejectedFiles}) => {
                                                    if (isDragAccept || isDragReject) {
                                                        return "Drop here to upload this file.";
                                                    }

                                                    return file ? file.name : 'Drop a CSV/XLSX file here or click to choose a file.';
                                                }}
                                            </DropZone>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            <Grid item container xs={12} justify={"flex-end"} alignItems={"flex-start"}
                                  style={{flex: 0}}>
                                <DownloadButton
                                    style={{padding: "10px"}}
                                    variant='contained'
                                    color='primary'
                                    onClick={() => {
                                        importItems();
                                    }}
                                    disabled={updating || !partnerId || !file}
                                >
                                    Import
                                </DownloadButton>
                            </Grid>
                        </Grid>

                        <Grid container item xs={5} style={{padding: "0px 14px", gap: "14px"}}>
                            {/* Templates */}
                            <Paper style={{width: "100%", borderRadius: "5px"}}>
                                <Grid container style={{gap: "14px", alignItems: "flex-start"}}>
                                    <Typography className={classes.sectionTitle}
                                                style={{textAlign: "left", padding: "24px 24px 0 16px"}}>
                                        Templates for {profile.Partner}
                                    </Typography>

                                    <Grid container>
                                        <Grid container style={{flexDirection: "column"}}>
                                            {/* Inventory Feed Template */}
                                            <Button style={{width: "100%"}}
                                                    onClick={() => {
                                                        if(retailerFields.length > 0) {
                                                            downloadTemplate("retailer");
                                                        } else {
                                                            downloadTemplate("standard");
                                                        }
                                                    }}
                                            >
                                                <Grid container
                                                      style={{flexDirection: "row", gap: "16px", alignItems: "center"}}>
                                                    <SpreadsheetIcon width="25" height="25"/>

                                                    <Grid style={{flexDirection: "column", textAlign: "left"}}>
                                                        <Typography style={{fontWeight: 500, fontSize: "16px"}}>
                                                            Inventory Feed Template
                                                        </Typography>
                                                        <Typography style={{
                                                            fontWeight: 400,
                                                            color: "#7F7D83",
                                                            fontSize: "14px"
                                                        }}>
                                                            Use this template to create a file to upload
                                                        </Typography>
                                                    </Grid>

                                                    <Grid container style={{flex: 1, justifyContent: "flex-end"}}>
                                                        {downloadingTemplate
                                                            ? <CircularProgress size={20}/>
                                                            : <FileDownloadIcon width="20" height="20"/>
                                                        }
                                                    </Grid>
                                                </Grid>
                                            </Button>

                                            {/* Retailer-Specific Inventory */}
                                            {/* {retailerFields && (
                                                <Button style={{width: "100%"}}
                                                        onClick={() => downloadTemplate("retailer")}
                                                >
                                                    <Grid container style={{
                                                        flexDirection: "row",
                                                        gap: "16px",
                                                        alignItems: "center"
                                                    }}>
                                                        <SpreadsheetIcon width="25" height="25"/>

                                                        <Grid style={{
                                                            flexDirection: "column",
                                                            textAlign: "left",
                                                            flex: 1
                                                        }}>
                                                            <Typography style={{fontWeight: 500, fontSize: "16px"}}>
                                                                Retailer-Specific Inventory
                                                            </Typography>
                                                            <Typography style={{
                                                                fontWeight: 400,
                                                                color: "#7F7D83",
                                                                fontSize: "14px"
                                                            }}>
                                                                A file with only the columns used by the retailer. You
                                                                can review the retailer's
                                                                specifications from the{' '}
                                                                <Link
                                                                    target='_blank'
                                                                    href={`/document-standards?partner=${props.isRetailer ? Auth.getAccountNumber() : partnerId}&type=inventory`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                >
                                                                    document standards page.
                                                                </Link>
                                                            </Typography>
                                                        </Grid>

                                                        <Grid container style={{flex: 0, justifyContent: "flex-end"}}>
                                                            <FileDownloadIcon width="20" height="20"/>
                                                        </Grid>
                                                    </Grid>
                                                </Button>
                                            )} */}

                                            {/* Matching SKU Feed Template */}
                                            <Button style={{width: "100%"}}
                                                    onClick={() => downloadTemplate("matched")}
                                            >
                                                <Grid container
                                                      style={{flexDirection: "row", gap: "16px", alignItems: "center"}}>
                                                    <SpreadsheetIcon width="25" height="25"/>

                                                    <Grid style={{flexDirection: "column", textAlign: "left"}}>
                                                        <Typography style={{fontWeight: 500, fontSize: "16px"}}>
                                                            Matching SKU Feed Template
                                                        </Typography>
                                                        <Typography style={{
                                                            fontWeight: 400,
                                                            color: "#7F7D83",
                                                            fontSize: "14px"
                                                        }}>
                                                            Use this template to assign a Retailer SKU to items
                                                        </Typography>
                                                    </Grid>

                                                    <Grid container style={{flex: 1, justifyContent: "flex-end"}}>
                                                        <FileDownloadIcon width="20" height="20"/>
                                                    </Grid>
                                                </Grid>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Specifications */}
                            <Paper style={{width: "100%", borderRadius: "5px",}}>
                                <Grid container style={{gap: "14px", alignItems: "flex-start"}}>
                                    <Typography className={classes.sectionTitle}
                                                style={{textAlign: "left", padding: "24px 24px 0 16px"}}>
                                        Specifications for {profile.Partner}
                                    </Typography>

                                    <Grid container>
                                        <Grid container style={{flexDirection: "column"}}>
                                            {/* Review Retailer's Requirements */}
                                            <Button style={{width: "100%"}}
                                                    component={'a'}
                                                    href={`/document-standards?type=Inventory&partner=${partnerId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                            >
                                                <Grid container
                                                      style={{flexDirection: "row", gap: "16px", alignItems: "center"}}>
                                                    <ListCheckmark width="25" height="25"/>

                                                    <Grid style={{flexDirection: "column", textAlign: "left"}}>
                                                        <Typography style={{fontWeight: 500, fontSize: "16px"}}>
                                                            Review Retailer's Requirements
                                                        </Typography>
                                                        <Typography style={{
                                                            fontWeight: 400,
                                                            color: "#7F7D83",
                                                            fontSize: "14px"
                                                        }}>
                                                            See the retailer's required and optional fields
                                                        </Typography>
                                                    </Grid>

                                                    <Grid container style={{flex: 1, justifyContent: "flex-end"}}>
                                                        <ChevronRight width="20" height="20"/>
                                                    </Grid>
                                                </Grid>
                                            </Button>

                                            {/* EDI Specifications */}
                                            {(ediSpecifications.length > 0 && ediSpecifications.find(spec => spec.PartnerCoId === partnerId)) && (
                                                <Button style={{width: "100%"}}
                                                        component={'a'}
                                                        href={ediSpecifications.find(spec => spec.PartnerCoId === partnerId).SpecLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                >
                                                    <Grid container style={{
                                                        flexDirection: "row",
                                                        gap: "16px",
                                                        alignItems: "center"
                                                    }}>
                                                        <ListCheckmark width="25" height="25"/>

                                                        <Grid style={{
                                                            flexDirection: "column",
                                                            textAlign: "left",
                                                            flex: 1
                                                        }}>
                                                            <Typography style={{fontWeight: 500, fontSize: "16px"}}>
                                                                EDI Specifications
                                                            </Typography>
                                                            <Typography style={{
                                                                fontWeight: 400,
                                                                color: "#7F7D83",
                                                                fontSize: "14px"
                                                            }}>
                                                                See the retailer's EDI 846 specifications
                                                            </Typography>
                                                        </Grid>

                                                        <Grid container style={{flex: 0, justifyContent: "flex-end"}}>
                                                            <ChevronRight width="20" height="20"/>
                                                        </Grid>
                                                    </Grid>
                                                </Button>
                                            )}

                                            {/* Knowledge Base */}
                                            <Button style={{width: "100%"}}
                                                    component='a'
                                                    target='_blank'
                                                    href={helpDesks.length > 0
                                                        ? `https://${helpDesks[0].Domain}.logicbroker.com/hc/en-us`
                                                        : 'https://help.logicbroker.com/hc/en-us'}
                                            >
                                                <Grid container
                                                      style={{flexDirection: "row", gap: "16px", alignItems: "center"}}>
                                                    <ListCheckmark width="25" height="25"/>

                                                    <Grid style={{flexDirection: "column", textAlign: "left"}}>
                                                        <Typography style={{fontWeight: 500, fontSize: "16px"}}>
                                                            Knowledge Base
                                                        </Typography>
                                                        <Typography style={{
                                                            fontWeight: 400,
                                                            color: "#7F7D83",
                                                            fontSize: "14px"
                                                        }}>
                                                            See platform documentation and browse articles
                                                        </Typography>
                                                    </Grid>

                                                    <Grid container style={{flex: 1, justifyContent: "flex-end"}}>
                                                        <ChevronRight width="20" height="20"/>
                                                    </Grid>
                                                </Grid>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Quick Bulk Actions */}
                            {(hasInventoryManagePermission && partnerId) && (
                                <Paper style={{width: "100%", borderRadius: "5px", padding: "24px",}}>
                                    <Grid container style={{gap: "14px", alignItems: "flex-start"}}>
                                        <Typography className={classes.sectionTitle} style={{textAlign: "left"}}>
                                            Quick Bulk Actions for {profile.Partner}
                                        </Typography>

                                        <Grid container spacing={2} style={{flexDirection: "column"}}>
                                            {/* Zero out Items */}
                                            <Grid item xs={12}>
                                                <Button variant="outlined" style={{width: "100%"}}
                                                        onClick={() => {
                                                            setConfirmZeroOutItems(true);
                                                        }}
                                                >
                                                    <Grid container
                                                          style={{flexDirection: "column", textAlign: "left"}}>
                                                        <Typography variant="h6" style={{fontWeight: 500}}>
                                                            Zero out Items
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary"
                                                                    style={{fontSize: "12px"}}>
                                                            Set quantity of all items on the feed to 0
                                                        </Typography>
                                                    </Grid>
                                                </Button>
                                            </Grid>

                                            {/* Delete All Items */}
                                            {(isBillable) && (
                                                <Grid item xs={12}>
                                                    <Button variant="outlined" style={{width: "100%"}}
                                                            onClick={() => {
                                                                setConfirmDeleteAllItems(true);
                                                            }}
                                                    >
                                                        <Grid container
                                                              style={{flexDirection: "column", textAlign: "left"}}>
                                                            <Typography variant="h6" style={{fontWeight: 500}}>
                                                                Delete All Items
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary"
                                                                        style={{fontSize: "12px"}}>
                                                                Delete all items from the feed
                                                            </Typography>
                                                        </Grid>
                                                    </Button>
                                                </Grid>
                                            )}

                                            {/* Clear All SKU Matches */}
                                            {(isBillable) && (
                                                <Grid item xs={12}>
                                                    <Button variant="outlined" style={{width: "100%"}}
                                                            onClick={() => {
                                                                setConfirmClearAllSkuMatches(true);
                                                            }}
                                                    >
                                                        <Grid container
                                                              style={{flexDirection: "column", textAlign: "left"}}>
                                                            <Typography variant="h6" style={{fontWeight: 500}}>
                                                                Clear All SKU Matches
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary"
                                                                        style={{fontSize: "12px"}}>
                                                                Remove the Retailer SKU from all items on feed
                                                            </Typography>
                                                        </Grid>
                                                    </Button>
                                                </Grid>
                                            )}

                                            {/* Reupload All Items */}
                                            <Grid item xs={12}>
                                                <Button variant="outlined" style={{width: "100%"}}
                                                        onClick={() => {
                                                            setConfirmReuploadAllItems(true);
                                                        }}
                                                >
                                                    <Grid container
                                                          style={{flexDirection: "column", textAlign: "left"}}>
                                                        <Typography variant="h6" style={{fontWeight: 500}}>
                                                            Reupload All Items
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary"
                                                                    style={{fontSize: "12px"}}>
                                                            Reupload a feed with all items with a positive quantity
                                                        </Typography>
                                                    </Grid>
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            )}

                            {/* Resources */}
                            <Paper style={{width: "100%", borderRadius: "5px"}}>
                                <Grid container style={{gap: "14px", alignItems: "flex-start"}}>
                                    <Typography className={classes.sectionTitle}
                                                style={{textAlign: "left", padding: "24px 24px 0 16px"}}>
                                        Resources
                                    </Typography>

                                    <Grid container>
                                        <Grid container style={{flexDirection: "column"}}>
                                            {/* Notifications */}
                                            <Button
                                                style={{width: "100%"}}
                                                component={'a'}
                                                href={`/profile/notifications`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <Grid container
                                                      style={{flexDirection: "row", gap: "16px", alignItems: "center"}}>
                                                    <Notifications
                                                        style={{width: '30px', height: '30px', color: "#7F7D83"}}/>

                                                    <Grid style={{flexDirection: "column", textAlign: "left"}}>
                                                        <Typography style={{fontWeight: 500, fontSize: "16px"}}>
                                                            Notifications
                                                        </Typography>
                                                        <Typography style={{
                                                            fontWeight: 400,
                                                            color: "#7F7D83",
                                                            fontSize: "14px"
                                                        }}>
                                                            Enabled Inventory Notifications
                                                        </Typography>
                                                    </Grid>

                                                    <Grid container style={{flex: 1, justifyContent: "flex-end"}}>
                                                        <ChevronRight
                                                            style={{width: '40px', height: '40px', color: "#7F7D83"}}/>
                                                    </Grid>
                                                </Grid>
                                            </Button>

                                            {/* Scheduled Reports */}
                                            {props.isBillable && (
                                                <Button
                                                    style={{width: "100%"}}
                                                    component={'a'}
                                                    href={`/settings/monitoring`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <Grid container style={{
                                                        flexDirection: "row",
                                                        gap: "16px",
                                                        alignItems: "center"
                                                    }}>
                                                        <BarChart
                                                            style={{width: '30px', height: '30px', color: "#7F7D83"}}/>

                                                        <Grid style={{
                                                            flexDirection: "column",
                                                            textAlign: "left",
                                                            flex: 1
                                                        }}>
                                                            <Typography style={{fontWeight: 500, fontSize: "16px"}}>
                                                                Scheduled Reports
                                                            </Typography>
                                                            <Typography style={{
                                                                fontWeight: 400,
                                                                color: "#7F7D83",
                                                                fontSize: "14px"
                                                            }}>
                                                                Configure inventory scheduled reports
                                                            </Typography>
                                                        </Grid>

                                                        <Grid container style={{flex: 0, justifyContent: "flex-end"}}>
                                                            <ChevronRight style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                color: "#7F7D83"
                                                            }}/>
                                                        </Grid>
                                                    </Grid>
                                                </Button>
                                            )}

                                            {/* Inventory FAQs */}
                                            <Button
                                                style={{width: "100%"}}
                                                component='a'
                                                target='_blank'
                                                rel="noopener noreferrer"
                                                href={`https://help.logicbroker.com/hc/en-us/articles/360022068531-Inventory-Feeds`}
                                            >
                                                <Grid container
                                                      style={{flexDirection: "row", gap: "16px", alignItems: "center"}}>
                                                    <InventoryFaq style={{width: '25px', height: '25px'}}/>

                                                    <Grid style={{flexDirection: "column", textAlign: "left"}}>
                                                        <Typography style={{fontWeight: 500, fontSize: "16px"}}>
                                                            Inventory FAQs
                                                        </Typography>
                                                        <Typography style={{
                                                            fontWeight: 400,
                                                            color: "#7F7D83",
                                                            fontSize: "14px"
                                                        }}>
                                                            See more information and frequently asked questions
                                                        </Typography>
                                                    </Grid>

                                                    <Grid container style={{flex: 1, justifyContent: "flex-end"}}>
                                                        <ChevronRight
                                                            style={{width: '40px', height: '40px', color: "#7F7D83"}}/>
                                                    </Grid>
                                                </Grid>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <UpdatingDialog open={updating} subtitle={updateMessage}/>

            <GenericDialog 
                open={updated} setOpen={() => setUpdated(false)} title={"Import Complete"}
                message={"Files uploaded, the import has been queued for processing."} />

            <EnhancedErrorDialog open={error.open} errorHeader={error.errorHeader} errorMessage={error.errorMessage}
                                 onClose={() => {
                                     setError({...error, open: false});
                                     if (loading) props.history.goBack();
                                 }}/>

            <EnhancedConfirmDialog open={confirmDeleteAllItems} setOpen={setConfirmDeleteAllItems}
                                   title="Delete All Items From Feed?"
                                   message={(
                                       <Typography>
                                           Before deleting items, please be sure to zero out the inventory for those
                                           items. Failure to do so may cause stock outs with remaining quantity in the
                                           retailer's system. Please make sure these items are removed from future
                                           feeds.
                                           <br/>
                                           <br/>
                                           Confirm the process to discontinue items with your partner.
                                       </Typography>
                                   )}
                                   type={"delete"}
                                   action={(action) => {
                                       if (action) removeAllItems()
                                   }}
                                   extraActionsBetween={(
                                       <Button variant='outlined' onClick={() => {
                                           setConfirmZeroOutItems(true);
                                           setConfirmDeleteAllItems(false);
                                       }}>
                                           Zero Out Inventory
                                       </Button>
                                   )}
            />

            <EnhancedConfirmDialog open={confirmZeroOutItems} setOpen={setConfirmZeroOutItems}
                                   action={(action) => {
                                       if (action) zeroOutItems();
                                   }}
                                   title="Zero out Items?"
                                   message={(
                                       <Typography>
                                           This action cannot be undone. Are you sure you want to update the quantity
                                           for all SKUs to 0?
                                       </Typography>
                                   )}
                                   type="caution"/>

            <EnhancedConfirmDialog open={confirmReuploadAllItems} setOpen={setConfirmReuploadAllItems}
                                   action={(action) => {
                                       if (action) reuploadAllItems();
                                   }}
                                   title="Reupload All Items?"
                                   message={(
                                       <Typography>
                                           This will reimport a file with all existing items in Logicbroker with a
                                           positive quantity and will update the 'Refreshed Within' field. Only proceed
                                           if the information from your previous feed has not changed. Are you sure you
                                           want to continue? </Typography>
                                   )}
                                   type="caution"/>

            <EnhancedConfirmDialog open={confirmClearAllSkuMatches} setOpen={setConfirmClearAllSkuMatches}
                                   action={(action) => {
                                       if (action) clearAllSkuMatches();
                                   }}
                                   title="Clear All SKU Matches?"
                                   message={(
                                       <Typography>
                                           This action cannot be undone. Are you sure you want to remove the Retailer
                                           SKU for all item? This may cause updates to not be sent to the retailer for
                                           those items. </Typography>
                                   )}
                                   type="caution"/>

            <EnhancedConfirmDialog open={confirmValidationBypass} setOpen={setConfirmValidationBypass}
                                   action={(action) => {
                                       if (action) {
                                           importItems(true);
                                       }
                                       setConfirmValidationBypass(false);
                                   }}
                                   title="Validation Warnings"
                                   message={(
                                       <Typography>
                                           There are validation warnings in your import file. Would you like to proceed with the import anyway?
                                           This may result in incomplete or incorrect data being imported.
                                           <br/>
                                           <br/>
                                           <b>Errors:</b>
                                           <br/>
                                           {validationWarning}
                                       </Typography>
                                   )}
                                   type="caution"/>

        </>
    )
}

export default withTheme(InventoryManagementImport);
